import { useEffect, useRef, useState } from 'react';
import axios, { InternalAxiosRequestConfig } from 'axios';
import './TopLoader.scss';

// Requests that shouldn't drive the bar (e.g. background polling) can opt out:
//   axios.get(url, { skipLoader: true } as any)
type LoaderConfig = InternalAxiosRequestConfig & { skipLoader?: boolean };

const SHOW_DELAY = 150; // don't flash the bar for near-instant requests
const TRICKLE_MS = 300;
const DONE_MS = 400; // fill to 100% + fade out

/**
 * Slim progress bar pinned to the top of the viewport. It stays visible while any
 * axios request is in flight, so every page gets it until its data has loaded.
 */
export function TopLoader() {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const pending = useRef(0);
  const timers = useRef<{ show?: number; trickle?: number; done?: number }>({});

  useEffect(() => {
    const t = timers.current;

    const start = () => {
      // a new request during the fade-out: keep the bar up instead of re-delaying it
      const stillShowing = t.done !== undefined;
      window.clearTimeout(t.done);
      t.done = undefined;
      if (t.show || t.trickle) return;
      t.show = window.setTimeout(() => {
        t.show = undefined;
        setVisible(true);
        setProgress(stillShowing ? 30 : 8);
        // ease toward 90% — never "finishes" until the requests actually do
        t.trickle = window.setInterval(() => {
          setProgress((p) => (p < 90 ? p + (90 - p) * 0.1 : p));
        }, TRICKLE_MS);
      }, stillShowing ? 0 : SHOW_DELAY);
    };

    const finish = () => {
      const shown = t.trickle !== undefined;
      window.clearTimeout(t.show);
      window.clearInterval(t.trickle);
      t.show = t.trickle = undefined;
      if (!shown) return; // finished inside SHOW_DELAY — bar never appeared
      setProgress(100);
      t.done = window.setTimeout(() => {
        t.done = undefined;
        setVisible(false);
        setProgress(0);
      }, DONE_MS);
    };

    // Track at the adapter rather than in interceptors: the adapter only runs for
    // requests that are actually sent (App.tsx's interceptor can reject some first,
    // without a config), and its promise always settles — so the count can't leak.
    const originalAdapter = axios.defaults.adapter;
    const send = axios.getAdapter(originalAdapter);
    axios.defaults.adapter = (config: LoaderConfig) => {
      if (config.skipLoader) return send(config);
      if (pending.current++ === 0) start();
      return send(config).finally(() => {
        pending.current = Math.max(0, pending.current - 1);
        if (pending.current === 0) finish();
      });
    };

    return () => {
      axios.defaults.adapter = originalAdapter;
      window.clearTimeout(t.show);
      window.clearInterval(t.trickle);
      window.clearTimeout(t.done);
    };
  }, []);

  return (
    <div
      className={`top-loader${visible ? ' active' : ''}${progress === 100 ? ' done' : ''}`}
      role="progressbar"
      aria-label="Loading"
      aria-hidden={!visible}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress)}
    >
      <div className="top-loader-bar" style={{ transform: `scaleX(${progress / 100})` }} />
    </div>
  );
}
